// Environment configuration
const ENVIRONMENTS = {
  staging: "https://dev-api.bleap.finance/api/",
  production: "https://api.bleap.finance/api/",
};

// Get current environment from localStorage, default to production
const getCurrentEnvironment = () => {
  return localStorage.getItem("environment") || "production";
};

// Get BASE_API based on current environment
const getBaseAPI = () => {
  const environment = getCurrentEnvironment();
  return ENVIRONMENTS[environment] || ENVIRONMENTS.production;
};

export const TWO_FA_ERROR_CODE = 2003;

// Generate a simple UUID and persist it in localStorage
const generateUUID = () => {
  let uuid = localStorage.getItem("device_uuid");
  if (uuid) {
    return uuid;
  }
  uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
  localStorage.setItem("device_uuid", uuid);
  return uuid;
};

// Device information for API headers
const getDeviceInfo = () => ({
  device_name: navigator.userAgent, // Use user agent as device name
  device_uuid: generateUUID(),
  platform: "Web",
});

const getBaseHeaders = () => {
  const deviceInfo = getDeviceInfo();
  return {
    "User-Agent": "BugsJoy/1.0.0",
    device_name: deviceInfo.device_name,
    device_uuid: deviceInfo.device_uuid,
    platform: deviceInfo.platform,
  };
};

// Helper function to check if token is about to expire (within 2 minutes)
const isTokenExpiringSoon = (token) => {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const expirationTime = payload.exp;

    // Return true if token expires within 2 minutes (120 seconds)
    return expirationTime - currentTime < 120;
  } catch (error) {
    console.error("Error parsing token:", error);
    return true;
  }
};

class AuthService {
  constructor() {
    this.token = localStorage.getItem("auth_token");
    this.refreshToken = localStorage.getItem("refresh_token");
    this.user = null;
    this.pendingEmail = null; // Store email during 2FA process
    this.refreshPromise = null; // Prevent multiple simultaneous refresh calls

    // Clean up invalid "undefined" tokens from storage
    if (this.token === "undefined") {
      this.token = null;
      localStorage.removeItem("auth_token");
    }
    if (this.refreshToken === "undefined") {
      this.refreshToken = null;
      localStorage.removeItem("refresh_token");
    }

    // Only restore user if we have a valid token
    if (this.token) {
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          this.user = JSON.parse(storedUser);
        }
      } catch (error) {
        console.error("Error parsing stored user data:", error);
        // Clear invalid data
        this.clearAuthData();
      }
    }

    // Bind methods to ensure `this` is always the service instance
    this.signIn = this.signIn.bind(this);
    this.signOut = this.signOut.bind(this);
    this.session = this.session.bind(this);
    this.onAuthStateChange = this.onAuthStateChange.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.getAuthHeaders = this.getAuthHeaders.bind(this);
    this.fetchUserSessions = this.fetchUserSessions.bind(this);
    this.fetchSessionLogs = this.fetchSessionLogs.bind(this);
    this.refreshTokens = this.refreshTokens.bind(this);
    this.ensureValidToken = this.ensureValidToken.bind(this);
    this.initializeSession = this.initializeSession.bind(this);
  }

  // Initialize session on app load: refresh the access token if it's expired
  // (or expiring soon) but a refresh token is still available.
  async initializeSession() {
    if (!this.token) {
      return null;
    }

    if (isTokenExpiringSoon(this.token)) {
      if (!this.refreshToken) {
        this.clearAuthData();
        return null;
      }

      try {
        await this.refreshTokens();
      } catch (error) {
        console.error("Failed to refresh token on app load:", error);
        this.clearAuthData();
        return null;
      }
    }

    return this.session();
  }

  // Helper method to clear all auth data
  clearAuthData() {
    this.token = null;
    this.refreshToken = null;
    this.user = null;
    this.pendingEmail = null;
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
  }

  // Helper method to store auth data
  storeAuthData(token, refreshToken, user) {
    this.token = token;
    this.refreshToken = refreshToken;
    this.user = user;

    localStorage.setItem("auth_token", token);
    if (refreshToken) {
      localStorage.setItem("refresh_token", refreshToken);
    }
    localStorage.setItem("user", JSON.stringify(user));
  }

  // Refresh tokens using the refresh token
  async refreshTokens() {
    // If there's already a refresh in progress, return that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.refreshToken) {
      throw new Error("No refresh token available");
    }

    this.refreshPromise = this._performTokenRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  async _performTokenRefresh() {
    try {
      const response = await fetch(`${getBaseAPI()}auth/refresh`, {
        method: "POST",
        headers: {
          ...getBaseHeaders(),
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.refreshToken}`,
        },
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error("Invalid response from server during token refresh");
      }

      if (!response.ok) {
        // If refresh fails, clear all auth data
        this.clearAuthData();
        const errorMessage =
          data?.error?.message || data?.message || "Token refresh failed";
        throw new Error(errorMessage);
      }

      // Extract new tokens from response
      const responseData = data.data || data;
      const newToken = responseData.token || responseData.access_token;
      const newRefreshToken =
        responseData.refresh_token || responseData.refreshToken;

      if (!newToken) {
        throw new Error("No access token received during refresh");
      }

      // Update stored tokens
      this.token = newToken;
      if (newRefreshToken) {
        this.refreshToken = newRefreshToken;
      }

      // Update localStorage
      localStorage.setItem("auth_token", this.token);
      if (newRefreshToken) {
        localStorage.setItem("refresh_token", this.refreshToken);
      }

      return {
        token: this.token,
        refreshToken: this.refreshToken,
        error: null,
      };
    } catch (error) {
      // Handle network errors and other fetch failures
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error("Network error: Unable to refresh token");
      }
      throw error;
    }
  }

  // Ensure we have a valid token, refresh if necessary
  async ensureValidToken() {
    if (!this.token) {
      throw new Error("No access token available");
    }

    // Check if token is expiring soon and refresh if needed
    if (isTokenExpiringSoon(this.token)) {
      try {
        await this.refreshTokens();
      } catch (error) {
        // If refresh fails, user needs to login again
        this.clearAuthData();
        throw new Error("Session expired. Please login again.");
      }
    }

    return this.token;
  }

  async signIn({ email, password, otpCode }) {
    try {
      // Prepare request body - always include email and pin, add otpCode if provided
      const requestBody = {
        email: email || this.pendingEmail, // Use current email or stored email during 2FA
        pin: password,
      };

      // Add otpCode if this is 2FA verification
      if (otpCode) {
        requestBody.otpCode = otpCode;
      }

      // Store email for 2FA process if this is initial login
      if (email && !otpCode) {
        this.pendingEmail = email;
      }

      const response = await fetch(`${getBaseAPI()}auth/v1/login`, {
        method: "POST",
        headers: {
          ...getBaseHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        throw new Error("Invalid response from server");
      }

      if (!response.ok) {
        // Check if this is a 2FA requirement - handle nested error structure
        const errorCode =
          data?.error?.internalErrorCode || data?.internalErrorCode;
        const errorMessage = data?.error?.message || data?.message;

        if (errorCode === TWO_FA_ERROR_CODE) {
          return {
            user: null,
            session: null,
            error: null,
            requiresOTP: true,
            message: errorMessage || "OTP required",
          };
        }

        // Handle other API error messages
        const finalErrorMessage =
          errorMessage || `Login failed (${response.status})`;
        throw new Error(finalErrorMessage);
      }

      // Successful authentication - store auth token and user data
      const responseData = data.data || data;
      const accessToken = responseData.token || responseData.access_token;
      const refreshToken =
        responseData.refresh_token || responseData.refreshToken;

      // Prevent storing an undefined token
      if (!accessToken) {
        this.pendingEmail = null; // Clear pending email on auth failure
        throw new Error(
          "Authentication successful, but no token was provided by the server.",
        );
      }

      const userEmail = email || this.pendingEmail; // Use email from current request or stored during 2FA

      const user = {
        id: responseData.user?.id || responseData.id,
        email: userEmail,
        user_metadata: {
          full_name:
            responseData.user?.name ||
            responseData.name ||
            userEmail?.split("@")[0],
          avatar_url: responseData.user?.avatar_url || null,
        },
        ...responseData.user,
      };

      // Store all auth data
      this.storeAuthData(accessToken, refreshToken, user);

      // Clear pending email after successful authentication
      this.pendingEmail = null;

      return {
        user: this.user,
        session: { access_token: this.token, refresh_token: this.refreshToken },
        error: null,
        requiresOTP: false,
      };
    } catch (error) {
      // Handle network errors and other fetch failures
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        return {
          user: null,
          session: null,
          error: "Network error: Unable to connect to server",
        };
      }
      return { user: null, session: null, error: error.message };
    }
  }

  async signOut() {
    this.clearAuthData();
    return { error: null };
  }

  session() {
    // Always check localStorage for the most current state
    if (this.token && this.user) {
      return {
        access_token: this.token,
        refresh_token: this.refreshToken,
        user: this.user,
      };
    }
    return null;
  }

  onAuthStateChange(callback) {
    // Simple auth state management - call callback with current session
    const session = this.session();
    if (session) {
      callback("SIGNED_IN", session);
    } else {
      callback("SIGNED_OUT", null);
    }

    // Return a mock listener object for compatibility
    return {
      data: {
        unsubscribe: () => {},
      },
    };
  }

  // Helper method to check if user is authenticated
  isAuthenticated() {
    return !!(this.token && this.user);
  }

  // Set environment (staging or production)
  setEnvironment(environment) {
    if (environment && ENVIRONMENTS[environment]) {
      localStorage.setItem("environment", environment);
      return true;
    }
    return false;
  }

  // Get current environment
  getEnvironment() {
    return getCurrentEnvironment();
  }

  // Get available environments
  getAvailableEnvironments() {
    return Object.keys(ENVIRONMENTS);
  }

  // Placeholder for signup - can be implemented when signup endpoint is provided
  async signUp(data) {
    throw new Error("Signup not implemented yet");
  }

  // Helper method to get authenticated headers with auto-refresh
  async getAuthHeaders() {
    await this.ensureValidToken();
    return {
      ...getBaseHeaders(),
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.token}`,
    };
  }

  // Fetch user sessions list with auto-refresh
  async fetchUserSessions(userId) {
    if (!this.token) {
      throw new Error("Not authenticated");
    }

    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${getBaseAPI()}backoffice/system/logs/user-sessions?userId=${userId}`,
        {
          method: "GET",
          headers,
          mode: "cors",
          credentials: "omit",
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.error?.message ||
            data?.message ||
            `Failed to fetch user sessions (${response.status})`,
        );
      }

      return data.data?.sessions || [];
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error("Network error: Unable to connect to server");
      }
      throw error;
    }
  }

  // Fetch session logs with auto-refresh
  async fetchSessionLogs(userId, sessionId) {
    if (!this.token) {
      throw new Error("Not authenticated");
    }

    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(
        `${getBaseAPI()}backoffice/system/logs/session-logs?userId=${userId}&sessionId=${sessionId}`,
        {
          method: "GET",
          headers,
          mode: "cors",
          credentials: "omit",
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.error?.message ||
            data?.message ||
            `Failed to fetch session logs (${response.status})`,
        );
      }

      return data.data || data;
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error("Network error: Unable to connect to server");
      }
      throw error;
    }
  }
}

// Create a singleton instance
const authService = new AuthService();

export { authService };
